import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import {
  FiUsers,
  FiUserX,
  FiClock,
  FiTrendingUp,
  FiRefreshCcw,
  FiSearch,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import './analytics.css';
import ClientPagination, { useClientPagination } from '../../components/common/ClientPagination';

const today = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());

const number = (value) => Number(value || 0).toLocaleString("en-IN");

const comparison = (value, inverse = false) => {
  if (value === null || value === undefined) {
    return { text: "No data last year", color: "text-gray-400" };
  }

  const amount = Number(value);
  const positive = inverse ? amount <= 0 : amount >= 0;

  return {
    text: `${amount >= 0 ? "+" : ""}${amount.toFixed(1)}% vs last year`,
    color: positive ? "text-green-600" : "text-red-600",
  };
};

const label = (date) =>
  date
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
      }).format(new Date(`${date}T00:00:00.000Z`))
    : "";

export default function AttendanceReport() {
  const { accessToken } = useAuth();
  const currentDate = today();

  const [dateFrom, setDateFrom] = useState(`${currentDate.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(currentDate);
  const [departmentId, setDepartmentId] = useState("");
  const [search, setSearch] = useState("");

  const [report, setReport] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadDepartments = useCallback(async () => {
    if (!accessToken) return;

    try {
      const response = await apiRequest("/departments", { accessToken });
      setDepartments(response || []);
    } catch {
      setDepartments([]);
    }
  }, [accessToken]);

  const loadReport = useCallback(
    async (refresh = false) => {
      if (!accessToken) return;

      try {
        refresh ? setRefreshing(true) : setLoading(true);
        setError("");

        const query = new URLSearchParams({
          from: dateFrom,
          to: dateTo,
        });

        if (departmentId) query.set("departmentId", departmentId);

        const response = await apiRequest(
          `/reports/attendance?${query.toString()}`,
          { accessToken },
        );

        setReport(response);
      } catch (requestError) {
        setError(requestError.message || "Unable to load attendance report.");
        setReport(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [accessToken, dateFrom, dateTo, departmentId],
  );

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const metrics = report?.metrics || {};
  const changes = report?.comparison?.changes || {};

  const employees = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (report?.employees || []).filter((employee) => {
      if (!query) return true;

      return (
        employee.name?.toLowerCase().includes(query) ||
        employee.employeeCode?.toLowerCase().includes(query) ||
        employee.department?.toLowerCase().includes(query)
      );
    });
  }, [report?.employees, search]);

  const cards = [
    {
      title: "Present Employees",
      value: number(metrics.presentEmployees),
      subtitle: {
        text: `${number(metrics.presentDays)} present days`,
        color: "text-green-600",
      },
      icon: <FiUsers size={22} />,
      color: "bg-green-50 text-green-600",
    },
    {
      title: "Employees With Absence",
      value: number(metrics.absentEmployees),
      subtitle: {
        text: `${number(metrics.absentDays)} absent days`,
        color: "text-red-600",
      },
      icon: <FiUserX size={22} />,
      color: "bg-red-50 text-red-600",
    },
    {
      title: "Late Employees",
      value: number(metrics.lateEmployees),
      subtitle: {
        text: `${number(metrics.lateDays)} late arrivals`,
        color: "text-yellow-600",
      },
      icon: <FiClock size={22} />,
      color: "bg-yellow-50 text-yellow-600",
    },
    {
      title: "Attendance Rate",
      value: `${Number(metrics.attendancePercentage || 0).toFixed(1)}%`,
      subtitle: {
        text: `${number(metrics.onLeaveEmployees)} employees on leave`,
        color: "text-primary-700",
      },
      icon: <FiTrendingUp size={22} />,
      color: "bg-primary-50 text-primary-700",
    },
  ];


  const pagination = useClientPagination(employees);
  return (
    <div className="charms-analytics space-y-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Attendance Analytics
          </h1>
          <p className="mt-2 text-gray-500">
            Attendance performance across departments and employees.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => loadReport(true)}
            disabled={refreshing}
            className="flex items-center gap-2 rounded-2xl border bg-white px-5 py-3 disabled:opacity-50"
          >
            <FiRefreshCcw className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>

        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(event) => setDateFrom(event.target.value)}
            className="rounded-2xl border border-gray-200 px-4 py-3"
          />

          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            max={currentDate}
            onChange={(event) => setDateTo(event.target.value)}
            className="rounded-2xl border border-gray-200 px-4 py-3"
          />

          <select
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
            className="rounded-2xl border border-gray-200 px-4 py-3"
          >
            <option value="">All Departments</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="rounded-3xl bg-white py-24 text-center text-gray-500">
          Loading attendance report...
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            {cards.map((card) => (
              <div
                key={card.title}
                className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{card.title}</p>
                    <h2 className="mt-2 text-3xl font-bold">{card.value}</h2>
                    <p
                      className={`mt-2 text-xs font-semibold ${card.subtitle.color}`}
                    >
                      {card.subtitle.text}
                    </p>
                  </div>
                  <div className={`rounded-2xl p-4 ${card.color}`}>
                    {card.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <section className="xl:col-span-2 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold">Daily Attendance Trend</h2>
              <p className="mb-5 text-sm text-gray-500">
                Attendance percentage for the selected dates.
              </p>

              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={report.attendanceTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={label} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="attendance"
                    name="Attendance %"
                    stroke="#7A2238"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </section>

            <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-xl font-bold">Department Attendance</h2>

              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={report.departmentAttendance || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar
                    dataKey="attendance"
                    name="Attendance %"
                    fill="#7A2238"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </section>
          </div>

          <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold">Employee Attendance</h2>
                <p className="text-gray-500">
                  Selected-period employee summary
                </p>
              </div>

              <div className="relative w-full lg:w-80">
                <FiSearch className="absolute left-4 top-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search employee..."
                  className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      "Employee",
                      "Department",
                      "Present",
                      "Absent",
                      "Late",
                      "On Leave",
                      "Attendance %",
                    ].map((heading) => (
                      <th key={heading} className="px-4 py-3 text-left text-sm">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {pagination.pageItems.map((employee) => (
                    <tr key={employee.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <p className="font-semibold">{employee.name}</p>
                        <p className="text-sm text-gray-500">
                          {employee.employeeCode}
                        </p>
                      </td>
                      <td className="px-4 py-4">{employee.department}</td>
                      <td className="px-4 py-4 text-green-600">
                        {employee.present}
                      </td>
                      <td className="px-4 py-4 text-red-600">
                        {employee.absent}
                      </td>
                      <td className="px-4 py-4 text-yellow-600">
                        {employee.late}
                      </td>
                      <td className="px-4 py-4 text-blue-600">
                        {employee.onLeave}
                      </td>
                      <td className="px-4 py-4 font-semibold">
                        {Number(employee.percentage || 0).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ClientPagination pagination={pagination} label="employees" />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
